import * as React from 'react';
import { UNKNOWN_META } from '@/lib/commerce-v2/meta';
import { endOfDay, startOfDay } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Info,
  Package,
  Search,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import type { MobileCardContext } from '@/components/reusable/DataTable';
import { Button } from '@/components/crm-ui';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  CustomDateRangePanel,
  useCustomDateRangePanel,
} from '@/components/ui/custom-date-range-panel';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { PillMeta } from '@/lib/commerce-v2/meta';

export function StatusPill({
  meta,
  label,
  icon,
  hint,
}: {
  meta: PillMeta;
  label?: string;
  icon?: React.ReactNode;
  hint?: React.ReactNode;
}) {
  const safeMeta = meta ?? UNKNOWN_META;
  const text = label ?? safeMeta.label;
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-[3px] text-[11.5px] font-semibold"
      style={{ color: safeMeta.color, background: safeMeta.bg }}
    >
      {icon ?? (
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ background: safeMeta.color }}
        />
      )}
      {text}
      {hint ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`About this status: ${text}`}
              className="-mr-0.5 inline-flex h-3.5 w-3.5 shrink-0 cursor-default items-center justify-center rounded-sm opacity-70 transition hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/40"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <Info className="h-3 w-3" strokeWidth={2.5} />
            </button>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent
              side="top"
              align="start"
              sideOffset={6}
              className="max-w-[260px] rounded-xl border border-[#EAECF0] bg-white px-3 py-2.5 text-[11.5px] font-medium leading-snug text-[#475467] shadow-[0_12px_28px_rgba(16,24,40,0.18)]"
            >
              {hint}
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      ) : null}
    </span>
  );
}

export function MaybePill({
  meta,
  icon,
}: {
  meta: PillMeta | null | undefined;
  icon?: React.ReactNode;
}) {
  if (!meta) return <span className="text-[#98A2B3]">—</span>;
  return <StatusPill meta={meta} icon={icon} />;
}

export function InfoHint({
  text,
  label,
}: {
  text: string;
  label?: string;
}) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label || text}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
            }}
            className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[#98A2B3] transition-colors hover:text-[#475467] focus-visible:text-[#475467] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/35"
          >
            <Info className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent
            side="top"
            className="max-w-[240px] border-[#EAECF0] bg-white px-2.5 py-1.5 text-[11.5px] font-medium leading-snug text-[#344054] shadow-[0_12px_28px_rgba(16,24,40,0.18)]"
          >
            {text}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
}

export type CommerceToastTone = 'success' | 'destructive' | 'warning' | 'info';

export function SortArrowButton({
  label,
  direction,
  onClick,
  testId,
}: {
  label: string;
  direction: 'asc' | 'desc' | null;
  onClick: () => void;
  testId?: string;
}) {
  const Icon =
    direction === 'asc'
      ? ChevronUp
      : direction === 'desc'
        ? ChevronDown
        : ChevronsUpDown;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Sort by ${label}`}
      title={`Sort by ${label}`}
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-1 rounded uppercase transition',
        direction ? 'text-brand' : 'text-[#98A2B3] hover:text-[#475467]',
      )}
    >
      {label}
      <Icon className="h-3 w-3 shrink-0" />
    </button>
  );
}

export function ToolbarSearch({
  value,
  onChange,
  placeholder,
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  testId?: string;
}) {
  return (
    <div className="relative flex h-9 w-full min-w-0 flex-1 items-center gap-2 rounded-lg border border-[#E4E7EC] bg-white px-2.5 sm:w-[260px] sm:min-w-[150px] sm:flex-[0_1_auto]">
      <Search className="h-4 w-4 shrink-0 text-[#98A2B3]" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="min-w-0 flex-1 bg-transparent text-[13px] text-[#101828] outline-none placeholder:text-[#98A2B3]"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          title="Clear search"
          className="shrink-0"
        >
          <X className="h-3.5 w-3.5 text-[#98A2B3] hover:text-[#101828]" />
        </button>
      )}
    </div>
  );
}

export function TableShell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('px-[15px] pt-4 pb-8', className)}>
      <div className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] max-sm:overflow-visible max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:shadow-none">
        {children}
      </div>
    </div>
  );
}

export function ProductThumb({
  name,
  imageUrl,
}: {
  name: string;
  imageUrl?: string | null;
}) {
  const [broken, setBroken] = React.useState(false);

  if (imageUrl && !broken) {
    return (
      <img
        src={imageUrl}
        alt=""
        aria-hidden
        onError={() => setBroken(true)}
        className="h-8 w-8 shrink-0 rounded-md border border-[#F2F4F7] object-cover"
      />
    );
  }

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();

  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[#F2F4F7] to-[#E4E7EC] text-[10px] font-bold text-[#344054]">
      {initials || <Package className="h-3.5 w-3.5 text-[#98A2B3]" />}
    </div>
  );
}

export function EmptyTable({
  title,
  detail,
  action,
  icon: Icon = Search,
}: {
  title: string;
  detail: string;
  action?: React.ReactNode;
  icon?: LucideIcon;
}) {
  return (
    <div className="px-6 py-16 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#F2F4F7] text-[#98A2B3]">
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-[14px] font-semibold text-[#101828]">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-[13px] text-[#667085]">
        {detail}
      </p>
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function BulkBar({
  count,
  onClear,
  children,
  note,
}: {
  count: number;
  onClear: () => void;
  children: React.ReactNode;
  note?: string;
}) {
  return (
    <AnimatePresence>
      {count > 0 && (
        <div className="pointer-events-none fixed bottom-6 left-0 right-0 z-40 flex justify-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className={cn(
              'pointer-events-auto rounded-2xl border border-black/10 bg-[#101828] text-white',
              'shadow-[0_16px_40px_rgba(16,24,40,0.24)]',
              'flex w-max max-w-[calc(100vw-2rem)] items-center gap-1 py-1.5 pl-3 pr-1.5',
            )}
            data-testid="commerce-v2-bulk-bar"
          >
            <span className="mr-1.5 inline-flex items-center gap-1 whitespace-nowrap text-[12px] font-semibold tabular-nums text-white/95">
              <Check className="h-3 w-3 opacity-80" strokeWidth={3} />
              {count} selected
            </span>
            {note && (
              <span className="hidden max-w-[280px] truncate px-1.5 text-[11.5px] text-white/70 sm:inline">
                {note}
              </span>
            )}
            <span className="h-6 w-px shrink-0 bg-white/15" />
            {children}
            <span className="h-6 w-px shrink-0 bg-white/15" />
            <button
              type="button"
              onClick={onClear}
              title="Clear selection"
              aria-label="Clear selection"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-white/70 transition hover:bg-white/10 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export function BulkBtn({
  children,
  onClick,
  icon,
  danger,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  icon?: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-[12.5px] font-semibold transition',
        'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40',
        danger
          ? 'text-[#FDA29B] hover:bg-white/10'
          : 'text-white/90 hover:bg-white/10',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

export function BulkNotice({
  title,
  detail,
  children,
}: {
  title: string;
  detail?: string;
  children?: React.ReactNode;
}) {
  const count = title.replace(/\D/g, '');
  return (
    <span
      className={cn(
        'group/notice relative -ml-0.5 inline-flex',
        children && 'cursor-not-allowed',
      )}
      aria-label={children && detail ? `${title}. ${detail}` : undefined}
    >
      {children ?? (
        <span
          className="inline-flex h-7 cursor-default items-center gap-1 rounded-full bg-[#F79009]/15 pl-1 pr-2 text-[#FDB022] transition hover:bg-[#F79009]/25"
          aria-label={detail ? `${title}. ${detail}` : title}
        >
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#F79009] text-white">
            <Info className="h-3 w-3" strokeWidth={2.5} />
          </span>
          {count && (
            <span className="text-[11px] font-semibold tabular-nums">
              {count}
            </span>
          )}
        </span>
      )}
      {children ? (
        <span className="absolute inset-0 z-[1]" aria-hidden />
      ) : null}
      <span className="pointer-events-none absolute bottom-[calc(100%+10px)] left-0 z-50 w-[260px] opacity-0 transition-opacity group-hover/notice:opacity-100">
        <span className="block rounded-xl border border-[#EAECF0] bg-white px-3 py-2.5 text-left shadow-[0_12px_28px_rgba(16,24,40,0.18)]">
          <span className="flex items-start gap-2">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#F79009] text-white">
              <Info className="h-2.5 w-2.5" strokeWidth={3} />
            </span>
            <span className="min-w-0">
              <span className="block text-[12px] font-semibold leading-snug text-[#101828]">
                {title}
              </span>
              {detail && (
                <span className="mt-0.5 block text-[11.5px] leading-snug text-[#667085]">
                  {detail}
                </span>
              )}
            </span>
          </span>
        </span>
        <span className="ml-3 mt-[-1px] block h-2.5 w-2.5 rotate-45 border-b border-r border-[#EAECF0] bg-white" />
      </span>
    </span>
  );
}

export const COMMERCE_V2_DRAWER_WIDTH = {
  widthPercent: 0.32,
  storageKey: 'commerce-v2:drawer:width',
} as const;

export function DrawerSection({
  title,
  children,
  right,
}: {
  title: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold text-[#101828]">{title}</h3>
        {right}
      </div>
      <div className="divide-y divide-[#F2F4F7] rounded-xl border border-[#EAECF0]">
        {children}
      </div>
    </section>
  );
}

export function KV({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 px-3.5 py-2">
      <span className="shrink-0 text-[12.5px] text-[#667085]">{label}</span>
      <span className="text-right text-[12.5px] font-medium text-[#101828]">
        {value}
      </span>
    </div>
  );
}

export function StatTile({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-[#EAECF0] bg-white px-3 py-2.5">
      <p className="text-[11px] font-medium text-[#667085]">{label}</p>
      <div className="mt-1 text-[13px] font-semibold text-[#101828]">
        {value}
      </div>
    </div>
  );
}


const DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
] as const;

export const ALL_DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last 7 days' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: '30d', label: 'Last 30 days' },
] as const;

export type DatePresetId = (typeof ALL_DATE_PRESETS)[number]['id'];

export const DATE_PRESET_OPTIONS = DATE_PRESETS.map((p) => ({
  id: p.id as string,
  label: p.label,
}));

export function dateRangeForPreset(
  preset: string,
  now: Date = new Date(),
): DateRangeValue | undefined {
  const to = endOfDay(now);
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  if (preset === 'today') return { from: startOfDay(now), to };
  if (preset === 'yesterday') {
    const day = new Date(y, m, d - 1);
    return { from: startOfDay(day), to: endOfDay(day) };
  }
  if (preset === '7d')
    return { from: startOfDay(new Date(y, m, d - 6)), to };
  if (preset === '30d') return { from: startOfDay(new Date(y, m, d - 29)), to };
  if (preset === 'this_month') return { from: startOfDay(new Date(y, m, 1)), to };
  if (preset === 'last_month')
    return { from: startOfDay(new Date(y, m - 1, 1)), to: endOfDay(new Date(y, m, 0)) };
  return undefined;
}

export function datePresetFor(
  value: DateRangeValue | undefined,
  now: Date = new Date(),
  presets: readonly { id: string; label: string }[] = DATE_PRESET_OPTIONS,
): string | null {
  if (!value?.from || !value?.to) return null;
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  for (const preset of presets) {
    const candidate = dateRangeForPreset(preset.id, now);
    if (
      candidate?.from &&
      candidate?.to &&
      sameDay(candidate.from, value.from) &&
      sameDay(candidate.to, value.to)
    ) {
      return preset.id;
    }
  }
  return null;
}


export interface DateRangeValue {
  from?: Date;
  to?: Date;
}

export function DateRangeFilterPill({
  label,
  value,
  onChange,
  testIdPrefix,
  presets = DATE_PRESETS,
}: {
  label: string;
  value: DateRangeValue | undefined;
  onChange: (range: DateRangeValue | undefined) => void;
  testIdPrefix: string;
  presets?: readonly { id: DatePresetId; label: string }[];
}) {
  const panel = useCustomDateRangePanel();

  const applyPreset = (preset: DatePresetId) => {
    onChange(dateRangeForPreset(preset));
    panel.closeAll();
  };

  const activePreset = datePresetFor(value, undefined, presets);

  const pillLabel = !value?.from && !value?.to
    ? label
    : activePreset
      ? (presets.find((p) => p.id === activePreset)?.label ?? 'Custom')
      : 'Custom';

  const active = Boolean(value?.from || value?.to);

  const isCustomRange = active && !activePreset;

  return (
    <>
      <Popover open={panel.open} onOpenChange={panel.setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex h-9 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-[13px] font-medium transition',
              active
                ? 'border-brand bg-brand-subtle text-brand-cta'
                : 'border-[#E4E7EC] bg-white text-[#344054] hover:bg-[#F9FAFB]',
            )}
            data-testid={`${testIdPrefix}-pill`}
          >
            {pillLabel}
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 text-[#98A2B3] transition-transform',
                panel.open && 'rotate-180',
              )}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-[240px] rounded-xl border border-[#E4E7EC] bg-white p-0 shadow-[0_8px_20px_rgba(16,24,40,0.08)]"
          {...panel.popoverContentProps}
        >
          <div ref={panel.menuRef} className="flex flex-col p-1.5">
            {presets.map((p) => {
              const selected = p.id === activePreset;
              return (
                <button
                  key={p.id}
                  type="button"
                  {...panel.presetRowProps}
                  onClick={() => applyPreset(p.id)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-[13px] font-medium transition',
                    selected
                      ? 'bg-brand-soft text-brand-cta'
                      : 'text-[#101828] hover:bg-[#F9FAFB]',
                  )}
                >
                  <span>{p.label}</span>
                  {selected ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
                </button>
              );
            })}
            <button
              type="button"
              {...panel.customRowProps}
              className={cn(
                'flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-[13px] font-medium transition',
                panel.showPanel || isCustomRange
                  ? 'bg-brand-soft text-brand-cta'
                  : 'text-[#101828] hover:bg-[#F9FAFB]',
              )}
            >
              <span>Custom date range</span>
              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
            </button>
          </div>
          {active ? (
            <div className="flex justify-end border-t border-[#F2F4F7] p-2">
              <button
                type="button"
                onClick={() => {
                  onChange(undefined);
                  panel.setOpen(false);
                }}
                className="h-8 rounded-lg px-2.5 text-[12.5px] font-semibold text-[#475467] transition hover:bg-[#F9FAFB]"
              >
                Clear
              </button>
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
      <CustomDateRangePanel
        {...panel.panelProps}
        value={value}
        testIdPrefix={testIdPrefix}
        onApply={(range) => {
          onChange(range);
          panel.closeAll();
        }}
        onCancel={panel.closePanel}
        onClear={() => onChange(undefined)}
        clearEnabled={active}
      />
    </>
  );
}

export function CommerceMobileCard({
  ctx,
  header,
  status,
  stats,
}: {
  ctx: MobileCardContext;
  header: React.ReactNode;
  status?: React.ReactNode;
  stats: Array<{ label: string; value: React.ReactNode }>;
}) {
  return (
    <div
      role={ctx.onClick ? 'button' : undefined}
      tabIndex={ctx.onClick ? 0 : undefined}
      onClick={ctx.onClick}
      onKeyDown={(e) => {
        if (!ctx.onClick || e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          ctx.onClick();
        }
      }}
      className={cn(
        'flex flex-col gap-3 rounded-2xl border bg-white p-4 text-left text-[13px] transition',
        ctx.onClick && 'cursor-pointer hover:border-[#D0D5DD] hover:shadow-[0_4px_14px_rgba(16,24,40,0.06)]',
        ctx.selected ? 'border-brand hover:border-brand' : 'border-[#EAECF0]',
      )}
    >
      <div className="flex items-center gap-2.5">
        {ctx.selectable ? (
          <span
            className="flex shrink-0"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={ctx.selected}
              onCheckedChange={(checked) => ctx.onSelectedChange(!!checked)}
              aria-label="Select row"
            />
          </span>
        ) : null}
        <div className="min-w-0 flex-1 overflow-hidden">{header}</div>
        {status ? <div className="shrink-0">{status}</div> : null}
        {ctx.actionsMenu ? (
          <div className="-mr-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            {ctx.actionsMenu}
          </div>
        ) : null}
      </div>

      {stats.length > 0 ? (
        <div
          className={cn(
            'grid gap-2 border-t border-[#F2F4F7] pt-3',
            stats.length >= 3 ? 'grid-cols-3' : 'grid-cols-2',
          )}
        >
          {stats.map((stat) => (
            <div key={stat.label} className="min-w-0">
              <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-[#98A2B3]">
                {stat.label}
              </p>
              <div className="mt-1 min-w-0 overflow-hidden text-[13px] font-semibold text-[#101828]">
                {stat.value}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
